/* Shop4Seats — vehicle fitment (deferred; only when Theme settings > Vehicle fitment is on).
 * Store:  cookie s4s_vehicle = { h, i (metaobject id), t, ti (type metaobject id), tn, y, mk, md }  →  window.S4S.vehicle.get/set/clear
 * Event:  document 's4s:vehicle-change' (detail = vehicle | null)
 * UI:     header chip / mobile button, <form data-vehicle-form> (Type → Year → Make → Model),
 *         fit lines: [data-fit="handle,handle,…"] (cards) and [data-fit-states] (product page: yes / no /
 *         unknown when the product has no fitment data / none when no vehicle is saved).
 * Vehicle list is fetched only when a form is first opened. */
(() => {
  const S4S = (window.S4S = window.S4S || {});
  const CFG = S4S.fitment || {};
  if (CFG.enabled === false) return;
  const COOKIE = 's4s_vehicle';
  const EVT = 's4s:vehicle-change';
  const CACHE = 's4s_vehicles_v5';

  /* ---------- Store ---------- */
  const read = () => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
    if (!m) return null;
    try { const v = JSON.parse(decodeURIComponent(m[1])); return v && v.t ? v : null; } catch (e) { return null; }
  };
  const write = (v) => {
    let c = `${COOKIE}=${encodeURIComponent(JSON.stringify(v))}; path=/; SameSite=Lax`;
    if (CFG.rememberDays > 0) c += `; max-age=${CFG.rememberDays * 86400}`;
    document.cookie = c;
  };
  const label = (v) => (v ? `${v.y} ${v.mk} ${v.md}` : '');
  S4S.vehicle = {
    get: read,
    label,
    set(v) { write(v); document.dispatchEvent(new CustomEvent(EVT, { detail: v })); },
    clear() { document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`; document.dispatchEvent(new CustomEvent(EVT, { detail: null })); },
    /* true / false, or null when no vehicle is saved. list = type + vehicle handles */
    fits(list) {
      const v = read();
      if (!v) return null;
      const arr = Array.isArray(list) ? list : String(list || '').split(',').map((x) => x.trim()).filter(Boolean);
      if (!arr.length) return null;
      return arr.includes(v.t) || arr.includes(v.h) || arr.includes(CFG.universal || 'all-vehicles');
    },
  };

  /* ---------- Vehicle data (lazy) ---------- */
  let dataP = null;
  const loadData = () => {
    if (dataP) return dataP;
    dataP = (async () => {
      try {
        const c = JSON.parse(sessionStorage.getItem(CACHE) || 'null');
        if (c) return c;
      } catch (e) { /* storage blocked */ }
      const out = { types: [], vehicles: [] };
      let page = 1;
      let pages = 1;
      do {
        const html = await fetch(`${S4S.routes.root}?section_id=fitment-vehicles&page=${page}`).then((r) => r.text());
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vehicles]');
        if (!el) throw new Error('No vehicle data');
        const d = JSON.parse(el.textContent);
        if (d.types.length) out.types = d.types;
        out.vehicles.push(...d.vehicles);
        pages = d.pages || 1;
        page += 1;
      } while (page <= pages);
      out.types.sort((a, b) => a.s - b.s || a.n.localeCompare(b.n));
      try { sessionStorage.setItem(CACHE, JSON.stringify(out)); } catch (e) { /* storage blocked */ }
      return out;
    })();
    dataP.catch(() => { dataP = null; });
    return dataP;
  };

  /* ---------- Form: Type → Year → Make → Model ---------- */
  const STEPS = ['type', 'year', 'make', 'model'];
  const now = new Date().getFullYear() + 1;
  function initForm(form) {
    if (form._init) return form._ready;
    form._init = true;
    const sel = Object.fromEntries(STEPS.map((s) => [s, form.querySelector(`[data-step="${s}"]`)]));
    const save = form.querySelector('[type="submit"]');
    const msg = form.querySelector('[data-vehicle-msg]');
    let data = null;
    const fill = (select, items, keep) => {
      const ph = select.options[0];
      select.replaceChildren(ph, ...items.map(([v, t]) => new Option(t, v)));
      select.disabled = !items.length;
      select.value = keep && items.some(([v]) => v === keep) ? keep : '';
    };
    const vehiclesFor = (s) => data.vehicles.filter((x) => (!s.type || x.t === s.type)
      && (!s.year || ((parseInt(x.ys, 10) || 0) <= s.year && s.year <= (parseInt(x.ye, 10) || now)))
      && (!s.make || x.mk === s.make));
    const state = () => ({ type: sel.type.value, year: parseInt(sel.year.value, 10) || 0, make: sel.make.value, model: sel.model.value });
    const refill = (from, keep = {}) => {
      const s = state();
      if (from === 'type') {
        const vs = vehiclesFor({ type: s.type });
        const yMin = Math.max(CFG.yearFrom || 1995, Math.min(...vs.map((x) => parseInt(x.ys, 10) || now)));
        const years = [];
        if (s.type) for (let y = now - 1; y >= yMin; y--) years.push([String(y), String(y)]);
        fill(sel.year, years, keep.year);
      }
      if (from === 'type' || from === 'year') {
        const s2 = state();
        const makes = s2.year ? [...new Set(vehiclesFor({ type: s2.type, year: s2.year }).map((x) => x.mk))].sort() : [];
        fill(sel.make, makes.map((m) => [m, m]), keep.make);
      }
      if (from !== 'model') {
        const s3 = state();
        const models = s3.make ? [...new Set(vehiclesFor(s3).map((x) => x.md))].sort() : [];
        fill(sel.model, models.map((m) => [m, m]), keep.model);
      }
      save.disabled = !Object.values(state()).every(Boolean);
    };
    STEPS.forEach((s) => sel[s].addEventListener('change', () => refill(s)));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const s = state();
      const hit = vehiclesFor(s).find((x) => x.md === s.model);
      if (!hit) return;
      const t = data.types.find((x) => x.h === s.type);
      S4S.vehicle.set({ h: hit.h, i: hit.i, t: s.type, ti: t ? t.i : '', tn: t ? t.n : '', y: s.year, mk: s.make, md: s.model });
      closeAll();
    });
    form.querySelector('[data-vehicle-clear]')?.addEventListener('click', () => S4S.vehicle.clear());
    form._prefill = () => {
      const v = read();
      if (!data) return;
      sel.type.value = v ? v.t : '';
      refill('type', v ? { year: String(v.y), make: v.mk, model: v.md } : {});
    };
    document.addEventListener(EVT, () => form._prefill()); // clear / change elsewhere → form shows the saved vehicle
    form._ready = loadData().then((d) => {
      data = d;
      fill(sel.type, d.types.map((t) => [t.h, t.n]));
      sel.type.disabled = false;
      form._prefill();
    }).catch(() => { if (msg) msg.textContent = 'Vehicle list could not load. Please try again.'; });
    return form._ready;
  }

  /* ---------- Panels (desktop popover / mobile panel) ---------- */
  const panels = () => document.querySelectorAll('[data-vehicle-panel]');
  const openers = () => document.querySelectorAll('[data-vehicle-open]');
  const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) || el.closest('.header__vehicle, .vehicle-panel');
  function closeAll() {
    panels().forEach((p) => { p.hidden = true; });
    openers().forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }
  function open(fromBtn) {
    const id = fromBtn && fromBtn.getAttribute('aria-controls');
    let panel = id && document.getElementById(id);
    if (!panel || !visible(panel.parentElement)) {
      // pick the panel that matches the current layout
      panel = matchMedia('(max-width: 989px)').matches ? document.getElementById('VehiclePanelMobile') : document.getElementById('VehiclePopover');
    }
    if (!panel) return;
    const wasOpen = !panel.hidden;
    closeAll();
    if (wasOpen && fromBtn && !fromBtn.hasAttribute('data-menu-close')) return;
    panel.hidden = false;
    openers().forEach((b) => { if (b.getAttribute('aria-controls') === panel.id) b.setAttribute('aria-expanded', 'true'); });
    const form = panel.querySelector('[data-vehicle-form]');
    if (form) initForm(form).then(() => form._prefill && form._prefill());
    if (panel.id === 'VehiclePanelMobile') panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    panel.querySelector('select:not([disabled])')?.focus({ preventScroll: true });
  }
  document.addEventListener('click', (e) => {
    const o = e.target.closest('[data-vehicle-open]');
    if (o) { e.preventDefault(); open(o); return; }
    if (e.target.closest('[data-vehicle-close]')) { closeAll(); return; }
    const pop = document.getElementById('VehiclePopover');
    if (pop && !pop.hidden && !e.target.closest('.header__vehicle')) pop.hidden = true;
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
  window.addEventListener('s4s-open-fit', () => open(null));
  // Inline forms (collection sidebar): load the vehicle list on first interaction
  const lazyInit = (e) => {
    const f = e.target.closest && e.target.closest('[data-vehicle-form]');
    if (f && !f.closest('[data-vehicle-panel]')) initForm(f);
  };
  document.addEventListener('pointerover', lazyInit, { passive: true });
  document.addEventListener('focusin', lazyInit);

  /* ---------- Render everything that shows the vehicle ---------- */
  function render() {
    const v = read();
    const name = label(v);
    document.querySelectorAll('[data-vehicle-label]').forEach((el) => { el.textContent = name; });
    document.querySelectorAll('[data-vehicle-chip]').forEach((el) => { el.hidden = !v; });
    document.querySelectorAll('[data-vehicle-add]').forEach((el) => { el.hidden = !!v; });
    document.querySelectorAll('[data-vehicle-current]').forEach((el) => { el.hidden = !v; });
    document.querySelectorAll('[data-vehicle-tick]').forEach((el) => { el.hidden = !v; });
    document.querySelectorAll('[data-if-vehicle]').forEach((el) => { el.hidden = !v; });
    document.querySelectorAll('[data-if-no-vehicle]').forEach((el) => { el.hidden = !!v; });
    document.querySelectorAll('[data-vehicle-line]').forEach((el) => { el.textContent = v ? `Showing fit for your ${name}` : 'Vehicle type, year, make and model'; });
    document.querySelectorAll('.vehicle-btn').forEach((el) => el.setAttribute('aria-label', v ? `My vehicle: ${name}` : 'Shop by your vehicle'));
    // Cards: [data-fit]
    document.querySelectorAll('[data-fit]').forEach((el) => {
      const f = S4S.vehicle.fits(el.dataset.fit);
      el.textContent = f === null ? '' : f ? `✓ Fits your ${name}` : `Doesn't fit your ${name}`;
      el.classList.toggle('is-yes', f === true);
      el.classList.toggle('is-no', f === false);
    });
    // Product page: [data-fit-states] with children [data-fit-state="yes|no|unknown|none"]
    // (unknown = vehicle saved but the product has no fitment data)
    document.querySelectorAll('[data-fit-states]').forEach((el) => {
      const f = S4S.vehicle.fits(el.dataset.fitStates);
      const st = !v ? 'none' : f === null ? 'unknown' : f ? 'yes' : 'no';
      el.querySelectorAll('[data-fit-state]').forEach((c) => { c.hidden = c.dataset.fitState !== st; });
      el.dataset.state = st;
    });
  }
  document.addEventListener(EVT, render);
  document.addEventListener('s4s:cart-updated', render);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render);
  else render();
})();
